import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Badge,
  Switch,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@b2b/ui-components";
import { adminService } from "@b2b/services";
import { ADMIN_STAFF_MODULE_CATALOG } from "../../config/SideBarItems.config";
import { SHOW_RFQ_FEATURES } from "../../config/featureFlags";

interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  onUserUpdated?: () => void;
  canManageStaff?: boolean;
}

export function AdminEditUser({
  open,
  onOpenChange,
  userId,
  onUserUpdated,
  canManageStaff = false,
}: EditUserModalProps) {
  const staffModuleOptions = ADMIN_STAFF_MODULE_CATALOG.filter(
    (moduleItem) => SHOW_RFQ_FEATURES || moduleItem.key !== "rfq",
  );
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    role: "",
    isActive: true,
    companyId: "",
    country: "",
    businessType: "",
    bio: "",
    website: "",
    linkedinProfile: "",
    profileImage: "",
    allowedModules: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalRole, setOriginalRole] = useState<string>("");

  // Load user data when modal opens
  useEffect(() => {
    if (open && userId) {
      loadUserData();
    }
  }, [open, userId]);

  // CRITICAL FIX: Ensure proper cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      // Force cleanup of any remaining overlays or body locks
      const cleanup = () => {
        // Remove any remaining dialog overlays
        const overlays = document.querySelectorAll(
          '[data-slot="dialog-overlay"]'
        );
        overlays.forEach((overlay) => {
          const element = overlay as HTMLElement;
          if (
            element.style.pointerEvents === "none" ||
            element.style.display === "none"
          ) {
            element.remove();
          }
        });

        // Ensure body styles are reset
        document.body.style.overflow = "";
        document.body.style.pointerEvents = "";

        // Remove any Radix UI data attributes that might block interactions
        const radixOverlays = document.querySelectorAll(
          "[data-radix-dialog-overlay]"
        );
        radixOverlays.forEach((overlay) => {
          const element = overlay as HTMLElement;
          if (
            !element.hasAttribute("data-state") ||
            element.getAttribute("data-state") === "closed"
          ) {
            element.remove();
          }
        });
      };

      // Use setTimeout to ensure cleanup happens after Radix UI's cleanup
      const timeoutId = setTimeout(cleanup, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  const loadUserData = async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      const user = await adminService.getUserById(userId);
      const userRole = user.role || "";
      setOriginalRole(userRole);
      setFormData({
        email: user.email || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
        role: userRole,
        isActive: user.isActive ?? true,
        companyId: user.companyId || "",
        country: (user as any).country || "",
        businessType: (user as any).businessType || "",
        bio: "",
        website: "",
        linkedinProfile: "",
        profileImage: "",
        allowedModules: Array.isArray((user as { allowedModules?: string[] }).allowedModules)
          ? ((user as { allowedModules?: string[] }).allowedModules as string[])
          : [],
      });
    } catch (error: any) {
      console.error("Error loading user data:", error);
      setError(error.message || "Failed to load user data");
    } finally {
      setIsLoading(false);
    }
  };

  // Wrapper for onOpenChange to ensure proper cleanup
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        // Clear error state when closing
        setError(null);
        // Reset form submission state
        setIsSubmitting(false);
        // Reset original role
        setOriginalRole("");
      }
      onOpenChange(newOpen);
    },
    [onOpenChange]
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    setIsSubmitting(true);
    setError(null);

    // Prevent changing to SUPER_ADMIN if user is not already SUPER_ADMIN
    if (originalRole !== "SUPER_ADMIN" && formData.role === "SUPER_ADMIN") {
      setError(
        "Cannot change user role to Super Admin. This role cannot be assigned via UI."
      );
      setIsSubmitting(false);
      return;
    }

    if (formData.role === "STAFF" && formData.allowedModules.length === 0) {
      setError("Select at least one module for staff");
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare update data (only include changed fields)
      const updateData: any = {};
      if (formData.firstName) updateData.firstName = formData.firstName;
      if (formData.lastName) updateData.lastName = formData.lastName;
      if (formData.phone !== undefined) updateData.phone = formData.phone;
      // Only include role if it has changed from the original role
      // Don't include role if user is already SUPER_ADMIN and keeping SUPER_ADMIN (backend rejects it)
      if (formData.role && formData.role !== originalRole) {
        updateData.role = formData.role;
      }
      if (formData.companyId) updateData.companyId = formData.companyId;
      if (formData.country) updateData.country = formData.country;
      if (formData.businessType)
        updateData.businessType = formData.businessType;
      if (formData.bio !== undefined) updateData.bio = formData.bio;
      if (formData.website !== undefined) updateData.website = formData.website;
      if (formData.linkedinProfile !== undefined)
        updateData.linkedinProfile = formData.linkedinProfile;
      if (formData.profileImage !== undefined)
        updateData.profileImage = formData.profileImage;
      updateData.isActive = formData.isActive;
      if (formData.role === "STAFF" && canManageStaff) {
        updateData.allowedModules = formData.allowedModules;
      }

      await adminService.updateUser(userId, updateData);

      if (onUserUpdated) {
        onUserUpdated();
      }

      handleOpenChange(false);
    } catch (error: any) {
      console.error("Error updating user:", error);
      setError(error.message || "Failed to update user. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    setIsSubmitting(false);
    handleOpenChange(false);
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl bg-white">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading user data...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-foreground">
            Edit User
          </DialogTitle>
        </DialogHeader>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User Details Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">User Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="Enter email address"
                    required
                    disabled
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => handleInputChange("role", value)}
                    disabled={originalRole === "SUPER_ADMIN"}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="BUYER">Buyer</SelectItem>
                      <SelectItem value="SELLER">Seller</SelectItem>
                      <SelectItem value="BUYER_SELLER">
                        Buyer & Seller
                      </SelectItem>
                      <SelectItem value="COMPANY_ADMIN">
                        Company Admin
                      </SelectItem>
                      <SelectItem
                        value="SUPER_ADMIN"
                        disabled={originalRole !== "SUPER_ADMIN"}
                      >
                        Super Admin
                      </SelectItem>
                      <SelectItem value="EMPLOYEE">Employee</SelectItem>
                      {(canManageStaff || originalRole === "STAFF") && (
                        <SelectItem value="STAFF">Staff</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {formData.role === "STAFF" && (
                  <div className="space-y-2 col-span-2">
                    <Label>
                      Allowed modules <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 p-3">
                      {staffModuleOptions.map((moduleItem) => {
                        const checked = formData.allowedModules.includes(
                          moduleItem.key,
                        );
                        return (
                          <label
                            key={moduleItem.key}
                            className="flex items-center gap-2 text-sm text-slate-700"
                          >
                            <Checkbox
                              checked={checked}
                              disabled={!canManageStaff}
                              onCheckedChange={(value) => {
                                if (!canManageStaff) return;
                                const next = value
                                  ? [...formData.allowedModules, moduleItem.key]
                                  : formData.allowedModules.filter(
                                      (key) => key !== moduleItem.key,
                                    );
                                handleInputChange("allowedModules", next);
                              }}
                            />
                            {moduleItem.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) =>
                      handleInputChange("country", value)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="India">India</SelectItem>
                      <SelectItem value="United States">
                        United States
                      </SelectItem>
                      <SelectItem value="United Kingdom">
                        United Kingdom
                      </SelectItem>
                      <SelectItem value="Germany">Germany</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                      <SelectItem value="Japan">Japan</SelectItem>
                      <SelectItem value="China">China</SelectItem>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="Canada">Canada</SelectItem>
                      <SelectItem value="Brazil">Brazil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyId">Company ID</Label>
                <Input
                  id="companyId"
                  value={formData.companyId}
                  onChange={(e) =>
                    handleInputChange("companyId", e.target.value)
                  }
                  placeholder="Enter company ID"
                />
              </div>
            </CardContent>
          </Card>

          {/* Account Settings Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Account Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="text-sm text-gray-500">
                    Enable or disable user account
                  </p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    handleInputChange("isActive", checked)
                  }
                />
              </div>
            </CardContent>
          </Card>

          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
